import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { companyApi } from '@/api';
import {
  Badge,
  BoxAvatar,
  BoxList,
  BoxRow,
  CardActions,
  Button,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Icon,
  LinkButton,
  ListCard,
  LoadingState,
  PageHeader,
  Pagination,
  SearchInput,
  SegmentedTabs,
  ViewButton,
  useToast,
} from '@/components/ui';
import { CandidateContactLine } from '@/features/candidates/CandidateContactLine';
import { MessageCandidateButton } from '@/features/messages/MessageCandidateButton';
import { errorMessage } from '@/hooks/useApiError';
import { paths } from '@/routes/paths';
import { formatDate, humanizeWorkMode } from '@/utils/format';

const PAGE_SIZE = 20;

/** How many skills a row shows before folding the rest into "+N". */
const SKILLS_SHOWN = 3;

export function TalentPoolPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [tag, setTag] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [removing, setRemoving] = useState(null);

  const params = {
    page,
    page_size: PAGE_SIZE,
    ...(tag ? { tag } : {}),
    ...(search.trim() ? { q: search.trim() } : {}),
  };

  const { data, isPending, isPlaceholderData, isError, error, refetch } = useQuery({
    queryKey: ['talent-pool', params],
    queryFn: () => companyApi.listTalentPool(params),
    // Keeps the last page on screen while the next one loads, so typing in
    // the search box does not swap the table for a spinner on every key.
    placeholderData: keepPreviousData,
  });

  const { data: tags } = useQuery({
    queryKey: ['candidate-tags'],
    queryFn: companyApi.listCandidateTags,
  });

  const remove = useMutation({
    mutationFn: (studentId) => companyApi.removeFromTalentPool(studentId),
    onSuccess: () => {
      toast.success('Removed from saved candidates. Your notes are kept.');
      void queryClient.invalidateQueries({ queryKey: ['talent-pool'] });
      void queryClient.invalidateQueries({ queryKey: ['candidate-tags'] });
      void queryClient.invalidateQueries({ queryKey: ['candidate-search'] });
      setRemoving(null);
    },
    onError: (removeError) =>
      toast.error(errorMessage(removeError, 'Could not remove the candidate')),
  });

  const activeCount = [tag !== '', search !== ''].filter(Boolean).length;

  function resetFilters() {
    setTag('');
    setSearch('');
    setPage(1);
  }

  const tagTabs = [
    { key: '', label: 'All' },
    ...(tags ?? []).map((entry) => ({
      key: entry.label,
      label: `#${entry.label}`,
      count: entry.count,
    })),
  ];

  const rows = data?.data ?? [];
  const meta = data?.meta;

  return (
    <div className="flex flex-col portrait:flex-1">
      <PageHeader
        title="Saved candidates"
        description="Candidates your team saved, with the tags you gave them."
      />

      <ListCard
        fill
        className="flex-1"
        isRefreshing={isPlaceholderData}
        toolbarStart={
          (tags ?? []).length > 0 ? (
            <SegmentedTabs
              variant="plain"
              label="Tag"
              tabs={tagTabs}
              value={tag}
              onChange={(key) => {
                setTag(key);
                setPage(1);
              }}
            />
          ) : null
        }
        toolbarEnd={
          <>
            <SearchInput
              label="Search saved candidates"
              hideLabel
              size="sm"
              className="w-full sm:w-64"
              placeholder="Name or headline"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
              onClear={() => {
                setSearch('');
                setPage(1);
              }}
            />
            {activeCount > 0 && (
              <Button variant="ghost" size="sm" onClick={resetFilters}>
                <Icon name="reset" size={14} />
                Reset
              </Button>
            )}
          </>
        }
        state={
          isError ? (
            <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />
          ) : isPending ? (
            <LoadingState label="Loading your saved candidates…" />
          ) : rows.length === 0 ? (
            activeCount > 0 ? (
              <EmptyState
                icon={<Icon name="users" size={22} />}
                title="No candidates"
                description="No saved candidates match these filters."
                action={
                  <Button variant="secondary" size="sm" onClick={resetFilters}>
                    Reset filters
                  </Button>
                }
              />
            ) : (
              <EmptyState
                icon={<Icon name="users" size={22} />}
                title="No saved candidates yet"
                description="Save candidates while searching and they show up here."
                action={
                  <LinkButton to={paths.company.candidates} size="sm">
                    Find Talent
                  </LinkButton>
                }
              />
            )
          ) : null
        }
        footer={
          meta && (
            <Pagination
              className=""
              page={meta.page}
              pageSize={PAGE_SIZE}
              totalPages={meta.total_pages}
              totalItems={meta.total_items}
              onPageChange={setPage}
            />
          )
        }
      >
        {/* Everyone here is saved, so every stripe is the "saved" green
            the search page uses. */}
        <BoxList>
          {rows.map((candidate) => (
            <BoxRow
              key={candidate.id}
              accent="success"
              leading={<BoxAvatar name={candidate.full_name} round />}
              title={candidate.full_name}
              to={paths.company.candidateDetail(candidate.id)}
              badges={
                candidate.application_count > 0 && (
                  <Badge tone="brand">
                    {candidate.application_count === 1
                      ? 'Applied to you'
                      : `${candidate.application_count} applications`}
                  </Badge>
                )
              }
              subtitle={candidate.headline}
              facts={[
                ...candidateFacts(candidate),
                ...(candidate.pool?.tags ?? []).map((entry) => ({
                  icon: 'tag',
                  text: `#${entry}`,
                  tone: 'brand',
                })),
              ]}
              meta={
                candidate.pool?.added_at && (
                  <span className="text-ink-muted text-xs">
                    Saved {formatDate(candidate.pool.added_at)}
                  </span>
                )
              }
              actions={
                <CardActions>
                  <MessageCandidateButton candidate={candidate} />
                  <ViewButton
                    to={paths.company.candidateDetail(candidate.id)}
                    title={`View ${candidate.full_name}'s profile`}
                    label="View profile"
                  />
                </CardActions>
              }
              corner={
                <Button
                  variant="ghostDanger"
                  size="sm"
                  title={`Remove ${candidate.full_name} from saved candidates`}
                  onClick={() => setRemoving(candidate)}
                >
                  <Icon name="trash" size={14} />
                  Remove
                </Button>
              }
            >
              <CandidateContactLine contact={candidate.contact} />
              <SkillBadges skills={candidate.top_skills} />
            </BoxRow>
          ))}
        </BoxList>
      </ListCard>

      <ConfirmDialog
        open={Boolean(removing)}
        title={`Remove ${removing?.full_name}?`}
        message="They leave your saved candidates and lose their tags. Notes your team wrote are kept."
        confirmLabel="Remove"
        isLoading={remove.isPending}
        onConfirm={() => remove.mutate(removing.id)}
        onCancel={() => setRemoving(null)}
      />
    </div>
  );
}

/** Degree and university, location and availability, and how complete the profile is. */
function candidateFacts(candidate) {
  const study = [candidate.degree, candidate.university].filter(Boolean).join(' · ');
  return [
    study && { icon: 'graduationCap', text: study },
    candidate.graduation_year && {
      icon: 'calendar',
      text: `Class of ${candidate.graduation_year}`,
    },
    candidate.location && { icon: 'home', text: candidate.location },
    candidate.preferred_work_mode && {
      icon: 'briefcase',
      text: humanizeWorkMode(candidate.preferred_work_mode),
    },
    candidate.available_from && {
      icon: 'calendarClock',
      text: `Available ${formatDate(candidate.available_from)}`,
    },
    {
      icon: 'userCheck',
      text: `${candidate.profile_completion ?? 0}% profile`,
      tone: (candidate.profile_completion ?? 0) >= 80 ? 'success' : undefined,
    },
  ];
}

function SkillBadges({ skills }) {
  if (!skills?.length) return null;
  const hidden = skills.length - SKILLS_SHOWN;
  return (
    <div className="mt-2.5 flex flex-wrap gap-1">
      {skills.slice(0, SKILLS_SHOWN).map((skill) => (
        <Badge key={skill} tone="brand">
          {skill}
        </Badge>
      ))}
      {hidden > 0 && (
        <Badge tone="neutral" title={skills.slice(SKILLS_SHOWN).join(', ')}>
          +{hidden}
        </Badge>
      )}
    </div>
  );
}
